import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';

import { apiFetch } from '../../api/client';
import { hasPermission } from '../../authProvider';

interface BusinessDetail {
  id: string;
  status: string;
  site_type: string;
  template_id: string | null;
  owner_user_id: string | null;
  owner_email: string | null;
  is_suspended: boolean;
}

interface TemplateOption {
  id: string;
  name: string;
  site_type: string | null;
}

interface QuotaStatus {
  used: number;
  limit: number;
  remaining: number;
  resets_at: string;
}

interface CustomHtmlData {
  variables: string[];
  html_template: string;
  values: Record<string, string>;
}

const SITE_TYPES = [
  { id: 'static', name: 'Static site' },
  { id: 'menu', name: 'Menu site' },
  { id: 'digital_catalog', name: 'Digital catalog' },
  { id: 'custom_html', name: 'HTML page (custom variables)' },
];

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export function OwnerAccessPanel() {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  const [detail, setDetail] = useState<BusinessDetail | null>(null);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [customHtml, setCustomHtml] = useState<CustomHtmlData | null>(null);

  const [selectedSiteType, setSelectedSiteType] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [quotaInput, setQuotaInput] = useState('');
  const [showOwnerForm, setShowOwnerForm] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ email: '', password: '', full_name: '' });
  const [busy, setBusy] = useState(false);

  const businessId = record?.id as string | undefined;

  const canWrite = hasPermission('business:write');
  const canPublish = hasPermission('business:publish');
  const canAssignTemplate = hasPermission('template:assign');
  const canSuspend = hasPermission('business:suspend');
  const canBlock = hasPermission('business:block');
  const canQuota = hasPermission('business:quota_manage');

  const loadDetail = async () => {
    if (!businessId) return;
    try {
      const data = await apiFetch<BusinessDetail>(`/admin/businesses/${businessId}`);
      setDetail(data);
      setSelectedSiteType(data.site_type);
      setSelectedTemplateId(data.template_id ?? '');
    } catch {
      // handled via notify at call sites
    }
  };

  const loadQuota = async () => {
    if (!businessId) return;
    try {
      const data = await apiFetch<QuotaStatus>(`/admin/businesses/${businessId}/quota`);
      setQuota(data);
      setQuotaInput(String(data.limit));
    } catch {
      // no quota row yet — fine, ignore
    }
  };

  const loadCustomHtml = async () => {
    if (!businessId) return;
    try {
      const data = await apiFetch<CustomHtmlData>(`/admin/businesses/${businessId}/custom-html`);
      setCustomHtml(data);
    } catch {
      setCustomHtml(null);
    }
  };

  useEffect(() => {
    if (!businessId) return;
    void loadDetail();
    void loadQuota();
    apiFetch<{ data: TemplateOption[] }>('/admin/templates')
      .then((res) => setTemplates(res.data))
      .catch(() => setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (detail?.site_type === 'custom_html') {
      void loadCustomHtml();
    } else {
      setCustomHtml(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.site_type, detail?.template_id]);

  if (!businessId) return null;

  const run = async (fn: () => Promise<void>, successMsg: string) => {
    setBusy(true);
    try {
      await fn();
      notify(successMsg, { type: 'success' });
      await loadDetail();
      refresh();
    } catch (err) {
      notify((err as Error).message || 'Something went wrong', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleCreateOwner = async (e: FormEvent) => {
    e.preventDefault();
    await run(async () => {
      await apiFetch(`/admin/businesses/${businessId}/create-owner`, {
        method: 'POST',
        body: ownerForm,
      });
      setShowOwnerForm(false);
      setOwnerForm({ email: '', password: '', full_name: '' });
    }, 'Owner account created');
  };

  const handleSaveSiteType = () =>
    run(async () => {
      await apiFetch(`/admin/businesses/${businessId}`, {
        method: 'PATCH',
        body: { site_type: selectedSiteType },
      });
    }, 'Site type updated');

  const handleAssignTemplate = () =>
    run(async () => {
      if (!selectedTemplateId) throw new Error('Pick a template first');
      await apiFetch(`/admin/templates/assign/${businessId}`, {
        method: 'POST',
        body: { template_id: selectedTemplateId },
      });
    }, 'Template assigned');

  const handleSuspend = () =>
    run(async () => {
      await apiFetch(`/admin/businesses/${businessId}/suspend`, { method: 'POST' });
    }, 'Business suspended (soft block)');

  const handleUnsuspend = () =>
    run(async () => {
      await apiFetch(`/admin/businesses/${businessId}/unsuspend`, { method: 'POST' });
    }, 'Business unsuspended');

  const handleBlock = () =>
    run(async () => {
      await apiFetch(`/admin/businesses/${businessId}/block`, { method: 'POST' });
    }, 'Business + owner login blocked (hard block)');

  const handleUnblock = () =>
    run(async () => {
      await apiFetch(`/admin/businesses/${businessId}/unblock`, { method: 'POST' });
    }, 'Business + owner login unblocked');

  const handlePublish = () =>
    run(async () => {
      await apiFetch(`/admin/businesses/${businessId}/publish`, { method: 'POST' });
    }, 'Business published — public page is now live');

  const handleQuotaSave = () =>
    run(async () => {
      const limit = parseInt(quotaInput, 10);
      if (Number.isNaN(limit)) throw new Error('Enter a valid number');
      await apiFetch(`/admin/businesses/${businessId}/quota`, {
        method: 'PATCH',
        body: { changes_limit: limit },
      });
      await loadQuota();
    }, 'Monthly edit limit updated');

  const handleCustomHtmlSave = () =>
    run(async () => {
      if (!customHtml) return;
      await apiFetch(`/admin/businesses/${businessId}/custom-html`, {
        method: 'PATCH',
        body: { values: customHtml.values },
      });
    }, 'Custom HTML values saved');

  return (
    <Box sx={{ width: 340, ml: 2 }}>
      <Stack spacing={2}>
        <SectionCard title="Owner account">
          {detail?.owner_email ? (
            <Chip label={detail.owner_email} color="success" variant="outlined" size="small" />
          ) : showOwnerForm ? (
            <Box component="form" onSubmit={handleCreateOwner}>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Full name"
                  required
                  value={ownerForm.full_name}
                  onChange={(e) => setOwnerForm({ ...ownerForm, full_name: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Email"
                  type="email"
                  required
                  value={ownerForm.email}
                  onChange={(e) => setOwnerForm({ ...ownerForm, email: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Temporary password"
                  type="password"
                  required
                  inputProps={{ minLength: 8 }}
                  value={ownerForm.password}
                  onChange={(e) => setOwnerForm({ ...ownerForm, password: e.target.value })}
                />
                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" size="small" disabled={busy}>
                    Create
                  </Button>
                  <Button size="small" onClick={() => setShowOwnerForm(false)}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ) : canWrite ? (
            <Button size="small" variant="outlined" onClick={() => setShowOwnerForm(true)}>
              Create owner account
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No owner linked yet.
            </Typography>
          )}
        </SectionCard>

        <SectionCard title="Site type">
          <Stack spacing={1.5}>
            <Select
              size="small"
              fullWidth
              value={selectedSiteType}
              onChange={(e) => setSelectedSiteType(e.target.value)}
            >
              {SITE_TYPES.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
            <Button
              size="small"
              variant="outlined"
              disabled={busy || !canWrite || selectedSiteType === detail?.site_type}
              onClick={handleSaveSiteType}
            >
              Save site type
            </Button>
          </Stack>
        </SectionCard>

        <SectionCard title="Template">
          <Stack spacing={1.5}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              <MenuItem value="">
                <em>Choose a template…</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} {t.site_type ? `(${t.site_type})` : ''}
                </MenuItem>
              ))}
            </Select>
            <Button
              size="small"
              variant="outlined"
              disabled={busy || !canAssignTemplate}
              onClick={handleAssignTemplate}
            >
              Assign template
            </Button>
            {detail?.template_id && (
              <Typography variant="caption" color="text.secondary">
                Current: {detail.template_id}
              </Typography>
            )}
          </Stack>
        </SectionCard>

        <SectionCard title="Publish">
          <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary">
              Public site is visible only when status is <strong>published</strong>.
              Current: <strong>{detail?.status}</strong>
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={busy || !canPublish || detail?.status === 'published'}
              onClick={handlePublish}
            >
              Publish business
            </Button>
          </Stack>
        </SectionCard>

        <SectionCard title="Access control">
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Status: <strong>{detail?.status}</strong>
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                color="warning"
                variant="outlined"
                disabled={busy || !canSuspend}
                onClick={handleSuspend}
              >
                Suspend
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={busy || !canSuspend}
                onClick={handleUnsuspend}
              >
                Unsuspend
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={busy || !canBlock}
                onClick={handleBlock}
              >
                Block (site + login)
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={busy || !canBlock}
                onClick={handleUnblock}
              >
                Unblock
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Suspend takes the public page offline only. Block also disables
              the owner's login.
            </Typography>
          </Stack>
        </SectionCard>

        <SectionCard title="Monthly self-edit quota">
          {quota ? (
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                Used {quota.used} / {quota.limit} (resets {quota.resets_at})
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  label="Limit"
                  value={quotaInput}
                  onChange={(e) => setQuotaInput(e.target.value)}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy || !canQuota}
                  onClick={handleQuotaSave}
                >
                  Save
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Loading…
            </Typography>
          )}
        </SectionCard>

        {detail?.site_type === 'custom_html' && (
          <SectionCard title="HTML page variables">
            {customHtml ? (
              customHtml.variables.length > 0 ? (
                <Stack spacing={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    These variable names are locked by the template and can't be
                    renamed — only their values below can change.
                  </Typography>
                  {customHtml.variables.map((v) => (
                    <TextField
                      key={v}
                      size="small"
                      label={`{${v}}`}
                      value={customHtml.values[v] ?? ''}
                      onChange={(e) =>
                        setCustomHtml({
                          ...customHtml,
                          values: { ...customHtml.values, [v]: e.target.value },
                        })
                      }
                    />
                  ))}
                  <Divider />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy || !canWrite}
                    onClick={handleCustomHtmlSave}
                  >
                    Save values
                  </Button>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  This template's custom_html section has no {'{variables}'} defined.
                </Typography>
              )
            ) : (
              <Typography variant="caption" color="text.secondary">
                Assign a template with a custom_html section to this business
                first (see Template section above).
              </Typography>
            )}
          </SectionCard>
        )}
      </Stack>
    </Box>
  );
}
